import { useEffect } from 'react';
import { useLang } from 'hooks/lang';
import { Link } from 'react-router-dom';

import { Notification } from 'entities';

import { Column, Row, Tag } from 'components/Blocks';

import { NotificationIcon, useNotification } from 'providers/Notifications';

import { useOnScreen } from 'hooks/isOnScreen';

import { getTimePassed } from 'utils/date';

interface SingleNotificationProps {
  children?: never;
  notification: Notification;
  index: number;
}

function fixType(type: string): string {
  const parts = type.split('_');

  // enable line break
  return parts.join('-');
}

function SingleNotification({ notification }: SingleNotificationProps) {
  const { t } = useLang();

  const { markNotificationAsRead } = useNotification();

  const { title, timestamp, read, link, descriptions, type } = notification;

  const [isVisible, setRef] = useOnScreen<HTMLAnchorElement>();

  useEffect(() => {
    if (!isVisible || read) return;

    markNotificationAsRead(notification);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <Link
      ref={setRef}
      to={link}
      className="flex gap-4 rounded-md border border-border p-4 pl-8 duration-500 animate-in fade-in slide-in-from-bottom-8 hover:bg-hover phone:flex-wrap"
    >
      <Column gap="1" alignItems="center" className="mt-1 phone:w-full phone:items-start">
        <NotificationIcon type={type} size={42} color="primary-dark" />

        {!read && <Tag color="primary" name={t('new')} />}
      </Column>

      <Column gap="2" fullWidth>
        <Row justifyContent="space-between">
          <p className="prose text-lg font-medium text-primary">{title}</p>

          <span className="self-start text-sm font-medium text-muted-foreground phone:hidden">
            {getTimePassed(timestamp)}
          </span>
        </Row>

        <span className="text-xs font-medium">
          {t('type')}: <span color="text-primary-dark">{fixType(type)}</span>
        </span>

        <Column gap="1" className="mt-2 text-sm phone:gap-2">
          {descriptions.map((description) => (
            <p key={description}>{description}</p>
          ))}
        </Column>
      </Column>

      <span className="hidden self-start text-xs font-medium text-muted-foreground phone:block">
        {getTimePassed(timestamp)}
      </span>
    </Link>
  );
}

export default SingleNotification;
